/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

export const queryKeys = {
  // Company & Config
  company: {
    all: ["company"],
  },
  config: {
    all: ["config"],
  },
  token: {
    all: ["token"],
  },
  translation: {
    byLang: (lang) => ["translation", lang],
  },
  plugins: {
    all: ["plugins"],
  },

  // Services & Providers
  services: {
    list: (params) => ["services", params],
  },
  providers: {
    list: (params) => ["providers", params],
  },
  provider: {
    detail: (id) => ["provider", id],
  },
  anyUnit: {
    all: ["any_unit"],
  },

  // Locations
  locations: {
    all: ["locations"],
  },
  location: {
    detail: (id) => ["location", id],
  },

  // Categories
  categories: {
    all: ["categories"],
  },

  // Scheduling
  schedule: {
    all: ["schedule"],
    list: (params) => ["schedule", { params }],
  },
  timeslots: {
    all: ["timeslots"],
    list: (params) => ["timeslots", params],
  },
  firstAvailableDay: {
    all: ["first_available_day"],
    list: (params) => ["first_available_day", { params }],
  },

  // Bookings
  booking: {
    all: ["booking"],
    detail: (id, hash) => ["booking", id, hash || null],
    list: (params) => ["booking", params],
    calendarLink: (id) => ["booking_calendar_link", id],
  },

  // Client
  client: {
    details: ["client_details"],
    bookingsAll: ["client_bookings"],
    bookings: (params) => ["client_bookings", params],
    fields: ["client_fields"],
    terms: ["client_terms"],
    userTerms: ["client_user_terms"],
    cancellationTerms: ["client_cancellation_terms"],
    privacyPolicy: ["client_privacy_policy"],
    personalDataStored: ["client_personal_data_stored"],
  },

  // Invoices
  invoices: {
    all: ["invoices"],
    list: (page) => ["invoices", page],
    unpaidCount: ["invoices", "unpaid_count"],
  },
  // Ids arrive as strings from the URL/callback hash and as numbers from the API - key them one way.
  invoice: {
    detail: (id) => ["invoice", id == null ? null : String(id)],
    withParams: (id, params) => ["invoice", id == null ? null : String(id), params || null],
  },
  packages: {
    all: ["packages"],
    byInvoice: (invoiceId) => ["packages", invoiceId],
  },
  paymentProcessors: {
    all: ["payment_processors"],
  },
  paymentMethods: {
    all: ["payment_methods"],
  },

  // Products
  products: {
    all: ["products_type_product"],
    list: (params) => ["products_type_product", params],
    categories: ["products_categories"],
  },

  // Additional
  additionalFields: {
    list: (params) => ["additional_fields", params],
  },

  // Geo
  countries: {
    all: ["countries"],
  },
  countryStates: {
    byCountry: (countryId) => ["country_states", countryId],
  },

  // Terms
  simplybookTerms: {
    all: ["simplybook_terms"],
  },

  // Online Meeting
  onlineMeeting: {
    detail: (id, hash) => ["online_meeting", id, hash || null],
  },

  // Promotions (gift cards)
  promotions: {
    list: (params) => ["promotions", params],
  },
  clientPromotions: {
    list: (page) => ["client_promotions", page],
  },

  // Memberships
  memberships: {
    list: (params) => ["memberships", params],
  },
  purchasedMemberships: {
    all: ["purchased_memberships"],
    list: (params) => ["purchased_memberships", params],
  },

  // Recurring profiles — one root, so invalidating `all` reaches a profile's
  // detail and its invoices too, and clientScopedQueryKeys drops the whole family.
  recurringProfiles: {
    all: ["recurring_profiles"],
    list: (page, onPage) => ["recurring_profiles", "list", page, onPage],
    detail: (id) => ["recurring_profiles", "detail", id],
    invoices: (id, page) => ["recurring_profiles", "detail", id, "invoices", page],
  },

  // Waiting list
  waitingList: {
    all: ["waiting_list"],
    list: (params) => ["waiting_list", params],
    item: (id) => ["waiting_list", "item", id],
  },
};


export const clientScopedQueryKeys = [
  queryKeys.client.details,
  queryKeys.client.personalDataStored,
  queryKeys.client.terms,
  queryKeys.client.userTerms,
  queryKeys.client.cancellationTerms,
  queryKeys.client.privacyPolicy,
  queryKeys.client.fields,
  queryKeys.client.bookingsAll,
  queryKeys.booking.all,
  queryKeys.invoices.all,
  queryKeys.purchasedMemberships.all,
  queryKeys.waitingList.all,
  queryKeys.paymentMethods.all,
  queryKeys.recurringProfiles.all,
];

export const mutationKeys = {
  // Booking
  createBooking: ["create_booking"],
  deleteBooking: (id) => ["delete_booking", id],
  rescheduleBooking: (id) => ["reschedule_booking", id],
  validateTime: ["validate_time"],

  // Client Auth
  clientLogin: ["client_login"],
  clientLoginByHash: ["client_login_by_hash"],
  clientLogout: ["client_logout"],
  clientRegister: ["client_register"],
  clientUpdate: ["client_update"],
  clientPasswordChange: ["client_password_change"],
  clientPasswordByHash: ["client_password_by_hash"],
  clientRemindPassword: ["client_remind_password"],
  clientPersonalData: ["client_personal_data"],

  // Invoice
  invoicePayFullAmount: (id) => ["invoice_pay_full_amount", id],
  invoicePayDepositAmount: (id) => ["invoice_pay_deposit_amount", id],
  invoicePromotion: (id) => ["invoice_promotion", id],
  invoicePromotionDelete: (id) => ["invoice_promotion_delete", id],
  invoiceAddPackageInstance: (id) => ["invoice_add_package_instance", id],
  invoiceRemovePackageInstance: (invoiceId) => ["invoice_remove_package_instance", invoiceId],
  invoiceTips: (id) => ["invoice_tips", id],
  invoiceCreate: ["invoice_create"],
  invoiceRebill: (id) => ["invoice_rebill", id],
  invoiceAddLine: ["invoice_add_line"],
  cancelRecurringProfile: (id) => ["cancel_recurring_profile", id],

  // Payment
  initPayment: (params) => ["init_payment", params],
  deletePaymentMethod: (id) => ["delete_payment_method", id],

  // GDPR
  gdprCode: ["gdpr_code"],

  // Waiting list
  joinWaitingList: ["join_waiting_list"],
  cancelWaitingListItem: (id) => ["cancel_waiting_list_item", id],
};
